import { useState } from "react";
import { Check, Loader2, Search, UserPlus } from "lucide-react";
import type { Prospect } from "@/lib/types";

function prospectKey(p: Prospect, idx: number) {
  return `${p.company_name}-${p.contact_email}-${idx}`;
}

export function ProspectingTab() {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [saved, setSaved] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState<Set<string>>(new Set());
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [bulkSaving, setBulkSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unsavedKeys = prospects
    .map((p, i) => prospectKey(p, i))
    .filter((k) => !saved.has(k));
  const allChecked = unsavedKeys.length > 0 && unsavedKeys.every((k) => checked.has(k));
  const someChecked = checked.size > 0;

  async function search(e: React.FormEvent) {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setError(null);
    setProspects([]);
    setSaved(new Set());
    setSaving(new Set());
    setChecked(new Set());
    try {
      const res = await fetch("/api/prospecting", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Search failed");
      setProspects(data.prospects ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Search failed");
    } finally {
      setLoading(false);
    }
  }

  async function saveOne(p: Prospect, key: string) {
    const [firstName, ...rest] = (p.contact_name ?? "").split(" ");
    const res = await fetch("/api/contacts", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        first_name: firstName || null,
        last_name: rest.join(" ") || null,
        email: p.contact_email,
        company: p.company_name,
        title: p.title,
        industry: p.industry,
        website: p.website,
        location: p.location,
        status: "discovered",
        source: "prospecting",
        notes: p.rationale,
      }),
    });
    return res.ok ? key : null;
  }

  async function saveProspect(p: Prospect, key: string) {
    setSaving((s) => new Set(s).add(key));
    const result = await saveOne(p, key);
    if (result) setSaved((s) => new Set(s).add(key));
    setSaving((s) => {
      const next = new Set(s);
      next.delete(key);
      return next;
    });
  }

  async function saveSelected() {
    setBulkSaving(true);
    const toSave = prospects
      .map((p, i) => ({ p, key: prospectKey(p, i) }))
      .filter(({ key }) => checked.has(key) && !saved.has(key));

    const savingKeys = new Set(toSave.map(({ key }) => key));
    setSaving((s) => new Set([...s, ...savingKeys]));

    const results = await Promise.allSettled(
      toSave.map(({ p, key }) => saveOne(p, key))
    );

    const newSaved = new Set<string>();
    for (const r of results) {
      if (r.status === "fulfilled" && r.value) newSaved.add(r.value);
    }

    setSaved((s) => new Set([...s, ...newSaved]));
    setSaving((s) => {
      const next = new Set(s);
      for (const k of savingKeys) next.delete(k);
      return next;
    });
    setChecked((s) => {
      const next = new Set(s);
      for (const k of newSaved) next.delete(k);
      return next;
    });
    setBulkSaving(false);
  }

  function toggleAll() {
    if (allChecked) {
      setChecked(new Set());
    } else {
      setChecked(new Set(unsavedKeys));
    }
  }

  function toggleOne(key: string) {
    setChecked((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border bg-card p-6">
        <div className="mb-4">
          <h2 className="text-base font-semibold">Prospecting</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Use AI to discover prospective businesses and contacts.
          </p>
        </div>
        <form onSubmit={search} className="flex gap-2">
          <input
            type="text"
            placeholder="e.g. Digital marketing agencies in Austin, TX with 10-50 employees"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1 rounded-md border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
          />
          <button
            type="submit"
            disabled={loading || !query.trim()}
            className="flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            Search
          </button>
        </form>
        {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
      </div>

      {prospects.length > 0 && (
        <div className="rounded-xl border bg-card">
          <div className="flex items-center justify-between border-b px-6 py-4">
            <h3 className="text-sm font-semibold">
              {prospects.length} prospects found
            </h3>
            {someChecked && (
              <button
                type="button"
                onClick={saveSelected}
                disabled={bulkSaving}
                className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {bulkSaving ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <UserPlus className="h-3 w-3" />
                )}
                {bulkSaving
                  ? "Saving..."
                  : `Save ${checked.size} selected`}
              </button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b text-left">
                  <th className="px-3 py-3 pl-6">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      onChange={toggleAll}
                      className="h-3.5 w-3.5 rounded border-gray-300 accent-primary"
                    />
                  </th>
                  <th className="px-6 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Company
                  </th>
                  <th className="px-6 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Industry
                  </th>
                  <th className="px-6 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Location
                  </th>
                  <th className="px-6 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground" />
                </tr>
              </thead>
              <tbody>
                {prospects.map((p, idx) => {
                  const key = prospectKey(p, idx);
                  const isSaved = saved.has(key);
                  const isSaving = saving.has(key);
                  const isChecked = checked.has(key);
                  return (
                    <tr
                      key={key}
                      className={`border-b last:border-b-0 transition-colors duration-300 ${
                        isSaved
                          ? "bg-green-50/60"
                          : isSaving
                            ? "bg-amber-50/40"
                            : "hover:bg-slate-50/60"
                      }`}
                    >
                      <td className="px-3 py-3 pl-6">
                        {isSaved ? (
                          <div className="flex h-3.5 w-3.5 items-center justify-center">
                            <Check className="h-3.5 w-3.5 text-green-600" />
                          </div>
                        ) : (
                          <input
                            type="checkbox"
                            checked={isChecked}
                            disabled={isSaving}
                            onChange={() => toggleOne(key)}
                            className="h-3.5 w-3.5 rounded border-gray-300 accent-primary disabled:opacity-50"
                          />
                        )}
                      </td>
                      <td className="px-6 py-3 text-sm">
                        <div className="font-medium">{p.company_name}</div>
                        {p.website && (
                          <a
                            href={
                              p.website.startsWith("http")
                                ? p.website
                                : `https://${p.website}`
                            }
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-muted-foreground hover:underline"
                          >
                            {p.website}
                          </a>
                        )}
                      </td>
                      <td className="px-6 py-3 text-sm">
                        <div className="font-medium">
                          {p.contact_name ?? "\u2014"}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {p.contact_email ?? ""}{" "}
                          {p.title ? ` \u00b7 ${p.title}` : ""}
                        </div>
                      </td>
                      <td className="px-6 py-3 text-sm">{p.industry ?? "\u2014"}</td>
                      <td className="px-6 py-3 text-sm">{p.location ?? "\u2014"}</td>
                      <td className="px-6 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => saveProspect(p, key)}
                          disabled={isSaved || isSaving}
                          className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-all duration-300 ${
                            isSaved
                              ? "border-green-200 bg-green-50 text-green-700"
                              : isSaving
                                ? "border-amber-200 bg-amber-50 text-amber-700"
                                : "bg-white hover:bg-slate-50"
                          } disabled:cursor-default`}
                        >
                          {isSaving ? (
                            <>
                              <Loader2 className="h-3 w-3 animate-spin" /> Saving...
                            </>
                          ) : isSaved ? (
                            <>
                              <Check className="h-3 w-3" /> Saved
                            </>
                          ) : (
                            <>
                              <UserPlus className="h-3 w-3" /> Save
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
